import React, { forwardRef, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Lista suspensa renderizada no body, posicionada (fixed) sob o `anchorRef`.
 * Usar no lugar de um `absolute top-full` quando o campo pode estar dentro de
 * um Modal/Dialog: lá o conteúdo tem `overflow-y-auto` e corta a lista, e o
 * overlay (z-[1300]) cobre qualquer coisa com z-index menor.
 * Abre para cima quando não há espaço abaixo. O `ref` aponta para a lista,
 * para o clique-fora do componente pai ignorar cliques dentro dela.
 */
const DropdownPortal = forwardRef(({ anchorRef, open, children, className = "" }, ref) => {
  const [style, setStyle] = useState(null);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const updatePosition = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const openUpwards = spaceBelow < 240 && spaceAbove > spaceBelow;
      setStyle({
        position: "fixed",
        left: rect.left,
        width: rect.width,
        top: openUpwards ? undefined : rect.bottom + 4,
        bottom: openUpwards ? viewportHeight - rect.top + 4 : undefined,
        maxHeight: Math.min(240, Math.max(120, (openUpwards ? spaceAbove : spaceBelow) - 12)),
      });
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, anchorRef]);

  if (!open) return null;

  return createPortal(
    <div
      ref={ref}
      style={style || { visibility: "hidden" }}
      className={`z-[9000] overflow-auto rounded-lg border border-farmazon-border bg-farmazon-surface py-1 shadow-lg ${className}`}
    >
      {children}
    </div>,
    document.body
  );
});

export default DropdownPortal;
